import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentDtoIn } from '@/models/in/DocumentDtoIn';
import { OpenFundRequestDocumentSlot } from '@/models/common/OpenFundRequestDocumentSlot';
import { OpenFundRequestPersonRole } from '@/models/common/OpenFundRequestPersonRole';
import { ReplaceOpenFundRequestDocumentDtoOut } from '@/models/out/ReplaceOpenFundRequestDocumentDtoOut';
import { usePutAuthApi } from '@/hooks/UseApiCalls';
import { EmptyFileError, FileReadError, FileReaderHelper, translateFileReadError } from '@/helpers/fileReaderHelper';
import Button from '@/components/ui/Button';
import CustomDialog from '@/components/ui/Dialog/Dialog';

import styles from './OpenFundRequestViewPage.module.scss';

/** Big enough for a phone photo of an ID card, small enough that a wrong file is caught before it is sent. */
const MAX_DOCUMENT_SIZE_MB = 15;

export interface IdDocumentTarget {
  personRole: OpenFundRequestPersonRole;
  personIndex: number;
  documentSlot: OpenFundRequestDocumentSlot;
  /** What the file is, in the words the page uses for it - e.g. 'צילום תעודת זהות של רכז 2'. */
  title: string;
  /** The file on record, when there is one, so the office sees what it is about to replace. */
  currentDocument?: DocumentDtoIn | null;
}

interface ReplaceIdDocumentDialogProps {
  requestId: number;
  target: IdDocumentTarget;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Uploads a single identity document onto a fund-opening request, in place of the one on record.
 *
 * Its own action rather than a field in the edit dialog, because an unreadable or outdated ID copy has to
 * be replaceable in every status of the request - including after the fund was created - while the details
 * around it stay locked to whatever the status allows.
 */
const ReplaceIdDocumentDialog = ({ requestId, target, onClose, onSaved }: ReplaceIdDocumentDialogProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { mutateAsync: replaceDocument } = usePutAuthApi<void, object, ReplaceOpenFundRequestDocumentDtoOut>(
    `funds/creation/${requestId}/document`
  );

  const isReplacing = !!target.currentDocument?.uri;

  const pickFile = (file: File | undefined) => {
    if (!file) return;

    setError(null);

    if (file.size === 0) {
      setSelectedFile(null);
      setError(t('emptyFileSelected', { name: file.name }));
      return;
    }

    if (file.size > MAX_DOCUMENT_SIZE_MB * 1024 * 1024) {
      setSelectedFile(null);
      setError(`הקובץ גדול מ-${MAX_DOCUMENT_SIZE_MB} מגה-בייט`);
      return;
    }

    setSelectedFile(file);
  };

  const save = async () => {
    if (!selectedFile) return;

    setError(null);
    setIsSaving(true);

    try {
      const [document] = await FileReaderHelper.fileToDocumentDtoOut(selectedFile, target.title);
      await replaceDocument({
        body: {
          personRole: target.personRole,
          personIndex: target.personIndex,
          documentSlot: target.documentSlot,
          document,
        },
      });
      onSaved();
      onClose();
    } catch (err) {
      if (err instanceof EmptyFileError || err instanceof FileReadError) {
        setError(translateFileReadError(err, t));
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      const status = e?.response?.status ?? e?.status;
      const data = e?.response?.data;
      const message = data?.message ?? (typeof data === 'string' ? data : null) ?? e?.message ?? 'שגיאה לא ידועה';
      setError(status ? `שגיאה ${status}: ${message}` : message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CustomDialog open setOpen={(value) => { if (!value) onClose(); }} hideActionButton>
      <div style={{ direction: 'rtl', textAlign: 'right', width: '100%' }}>
        <h3 className="heading-h4-semibold mb-4">{isReplacing ? 'החלפת מסמך' : 'העלאת מסמך'}</h3>
        <p className="body-md-regular mb-2">{target.title}</p>

        {isReplacing && (
          <p className="body-sm-regular mb-4">
            הקובץ הקיים:{' '}
            <a href={target.currentDocument!.uri} target="_blank" rel="noopener noreferrer" className={styles.docChipLink}>
              {target.currentDocument!.name}
            </a>
          </p>
        )}

        <div className="flex items-center gap-3 mb-2">
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isSaving}>
            בחירת קובץ
          </Button>
          <span className="body-sm-regular text-(--font-body-3) truncate">
            {selectedFile ? selectedFile.name : 'לא נבחר קובץ'}
          </span>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => {
            pickFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />

        {error && <div className={styles.errorText}>{error}</div>}

        <div className={styles.actionsBar}>
          <Button variant="primary" onClick={save} disabled={!selectedFile || isSaving}>
            {isSaving ? t('saving') : t('save')}
          </Button>
          <Button variant="white" onClick={onClose} disabled={isSaving}>
            {t('cancel')}
          </Button>
        </div>
      </div>
    </CustomDialog>
  );
};

export default ReplaceIdDocumentDialog;
